import React from 'react';
import { TestResult } from '../../../../testing';
import { buttonStyle, primaryButtonStyle, selectStyle, sectionHeaderStyle, resultItemStyle } from './styles';

interface TestScenario {
  name: string;
  description: string;
}

interface ScenarioRunnerProps {
  isRunning: boolean;
  selectedScenario: string;
  availableScenarios: TestScenario[];
  testResults: TestResult[];
  showVisualFeedback: boolean;
  onSetSelectedScenario: (name: string) => void;
  onSetShowVisualFeedback: (show: boolean) => void;
  onRunAll: () => void;
  onRunSingle: (name: string) => void;
  onClear: () => void;
  onExport: () => void;
}

export const ScenarioRunner: React.FC<ScenarioRunnerProps> = ({
  isRunning,
  selectedScenario,
  availableScenarios,
  testResults,
  showVisualFeedback,
  onSetSelectedScenario,
  onSetShowVisualFeedback,
  onRunAll,
  onRunSingle,
  onClear,
  onExport
}) => {
  const passedCount = testResults.filter(r => r.passed).length;
  const failedCount = testResults.filter(r => !r.passed).length;

  return (
    <>
      <div style={sectionHeaderStyle}>Test Runner</div>

      <button
        style={primaryButtonStyle}
        onClick={onRunAll}
        disabled={isRunning}
      >
        {isRunning ? '⏳ Running...' : '🧪 Run All Tests'}
      </button>

      <div style={{ display: 'flex', gap: '5px' }}>
        <select
          style={{ ...selectStyle, flex: 1 }}
          value={selectedScenario}
          onChange={(e) => onSetSelectedScenario(e.target.value)}
          disabled={isRunning}
        >
          <option value="">Select Scenario...</option>
          {availableScenarios.map(scenario => (
            <option key={scenario.name} value={scenario.name}>
              {scenario.name}
            </option>
          ))}
        </select>
        <button
          style={{ ...buttonStyle, width: 'auto', marginBottom: 8 }}
          onClick={() => selectedScenario && onRunSingle(selectedScenario)}
          disabled={!selectedScenario || isRunning}
        >
          ▶️
        </button>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '10px',
        fontSize: '11px',
        color: '#888'
      }}>
        <input
          type="checkbox"
          id="visualFeedback"
          checked={showVisualFeedback}
          onChange={(e) => onSetShowVisualFeedback(e.target.checked)}
        />
        <label htmlFor="visualFeedback">Show Visual Feedback</label>
      </div>

      <div style={sectionHeaderStyle}>Results</div>

      {testResults.length > 0 && (
        <div style={{
          display: 'flex',
          gap: '5px',
          marginBottom: '10px',
          fontSize: '11px'
        }}>
          <span style={{ color: '#2ecc71' }}>✅ {passedCount}</span>
          <span style={{ color: '#666' }}>|</span>
          <span style={{ color: '#e74c3c' }}>❌ {failedCount}</span>
          <span style={{ color: '#666' }}>|</span>
          <span style={{ color: '#888' }}>
            ⏱️ {testResults.reduce((sum, r) => sum + r.duration, 0).toFixed(0)}ms
          </span>
        </div>
      )}

      <div style={{ maxHeight: '200px', overflowY: 'auto', background: 'rgba(0,0,0,0.2)', padding: '5px' }}>
        {testResults.length === 0 ? (
          <div style={{ padding: '10px', textAlign: 'center', color: '#666', fontSize: '11px' }}>No Results</div>
        ) : (
          testResults.map((result, idx) => (
            <div key={idx} style={resultItemStyle(result.passed)}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{result.passed ? '✅' : '❌'} {result.name}</span>
                <span style={{ color: '#888' }}>{result.duration.toFixed(0)}ms</span>
              </div>
              {!result.passed && result.error && (
                <div style={{ color: '#e74c3c', marginTop: '4px', fontSize: '10px' }}>
                  {result.error}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {testResults.length > 0 && (
        <div style={{ display: 'flex', gap: '5px', marginTop: '10px' }}>
          <button style={{ ...buttonStyle, flex: 1, background: '#27ae60', borderColor: '#2ecc71', textAlign: 'center' }} onClick={onExport}>
            📥 Export Report
          </button>
          <button style={{ ...buttonStyle, flex: 1, background: '#c0392b', borderColor: '#e74c3c', textAlign: 'center' }} onClick={onClear}>
            🗑️ Clear
          </button>
        </div>
      )}
    </>
  );
};
